import { POPULAR_EMOJI_NAMES } from "@productlane/lib/src/emoji"

export interface FrequentEmoji {
	name: string
	imageUrl: string | null
	count: number
	lastUsedAt: number
}

/** Two picker rows. */
export const FREQUENT_EMOJI_LIMIT = 18
const MAX_TRACKED = 60

const storageKey = (userId: string) => `frequent-emoji:${userId}`

export function readFrequentEmoji(userId: string): FrequentEmoji[] {
	try {
		const raw = window.localStorage.getItem(storageKey(userId))
		const parsed: unknown = raw ? JSON.parse(raw) : []
		return Array.isArray(parsed) ? (parsed as FrequentEmoji[]) : []
	} catch {
		return []
	}
}

/** Most used first, then most recent; popular emoji fill the rows for someone who hasn't reacted much yet. */
export function topFrequentEmoji(
	used: readonly FrequentEmoji[],
	limit = FREQUENT_EMOJI_LIMIT,
): { name: string; imageUrl: string | null }[] {
	const ranked = [...used]
		.sort((a, b) => b.count - a.count || b.lastUsedAt - a.lastUsedAt)
		.slice(0, limit)
		.map(({ name, imageUrl }) => ({ name, imageUrl }))
	const taken = new Set(ranked.map((emoji) => emoji.name))
	const fill = POPULAR_EMOJI_NAMES.filter((name) => !taken.has(name))
		.slice(0, limit - ranked.length)
		.map((name) => ({ name, imageUrl: null }))
	return [...ranked, ...fill]
}

export function recordEmojiUse(
	userId: string,
	emoji: { name: string; imageUrl: string | null },
	now = Date.now(),
) {
	const used = readFrequentEmoji(userId)
	const existing = used.find((entry) => entry.name === emoji.name)
	const next = existing
		? used.map((entry) =>
				entry === existing
					? {
							...entry,
							imageUrl: emoji.imageUrl,
							count: entry.count + 1,
							lastUsedAt: now,
						}
					: entry,
			)
		: [...used, { ...emoji, count: 1, lastUsedAt: now }]
	// Keep the busiest ones so the list never grows without bound.
	const kept = next
		.sort((a, b) => b.count - a.count || b.lastUsedAt - a.lastUsedAt)
		.slice(0, MAX_TRACKED)
	try {
		window.localStorage.setItem(storageKey(userId), JSON.stringify(kept))
	} catch {
		/* private mode or full storage: the defaults still show */
	}
}
